import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Sidebar } from '../../components/Sidebar';
import { Button } from '../../components/ui/button';
import { Loader2, Megaphone, IndianRupee, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Creator-facing inbox of bulk campaign invites.
// Statuses surfaced: invited / no_response (actionable) and others (read-only history).
const STATUS_PILL = {
  invited:           { label: 'Invite waiting',    color: '#FFB800' },
  no_response:       { label: 'Awaiting reply',    color: '#FFB800' },
  interested:        { label: 'Accepted',          color: '#0055FF' },
  rejected:          { label: 'Declined',          color: '#FF3B30' },
  approved_by_brand: { label: 'Brand approved',    color: '#FFB800' },
  product_shipped:   { label: 'Product shipped',   color: '#A855F7' },
  product_delivered: { label: 'Product delivered', color: '#EC4899' },
  draft_submitted:   { label: 'Draft in review',   color: '#06B6D4' },
  revision_required: { label: 'Revision needed',   color: '#F97316' },
  approved:          { label: 'Draft approved',    color: '#84CC16' },
  posted:            { label: 'Posted',            color: '#22C55E' },
  completed:         { label: 'Completed',         color: '#00C972' },
};

export const CreatorBulkInvites = () => {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/bulk/my-invites`, { withCredentials: true });
      setInvites(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load invites');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const respond = async (rowId, decision) => {
    setBusyId(rowId);
    try {
      await axios.post(`${API_URL}/api/bulk/creators/${rowId}/respond`, { decision }, { withCredentials: true });
      toast.success(decision === 'accept' ? 'Invite accepted' : 'Invite declined');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setBusyId(null);
    }
  };

  const pending = invites.filter((i) => ['invited', 'no_response'].includes(i.status));
  const accepted = invites.filter((i) => !['invited', 'no_response'].includes(i.status));

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]" data-testid="creator-bulk-invites-page">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold tracking-tight text-[#0F0F0F] dark:text-white mb-2 flex items-center gap-3">
            <Megaphone className="text-[#FF451A]" size={32} />
            Bulk Campaign Invites
          </h1>
          <p className="text-[#6B6B6B] dark:text-zinc-400 mb-8">Accept or decline fixed-cost campaigns from brands.</p>

          {loading ? (
            <div className="py-20 text-center"><Loader2 className="inline-block animate-spin text-[#FF451A]" size={28} /></div>
          ) : invites.length === 0 ? (
            <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-16 text-center" data-testid="invites-empty-state">
              <Megaphone className="mx-auto text-[#6B6B6B] dark:text-zinc-500 mb-3" size={44} />
              <h3 className="text-lg font-semibold text-[#0F0F0F] dark:text-white mb-1">No invites yet</h3>
              <p className="text-sm text-[#6B6B6B] dark:text-zinc-400">Bulk campaign invites from brands will show up here.</p>
            </div>
          ) : (
            <>
              {pending.length > 0 && (
                <section className="mb-8" data-testid="invites-pending-section">
                  <div className="text-[10px] uppercase tracking-wider text-[#FFB800] mb-3 font-mono">Awaiting your response · {pending.length}</div>
                  <div className="space-y-3">
                    {pending.map((row) => (
                      <InviteCard key={row.id} row={row} onRespond={respond} busy={busyId === row.id} />
                    ))}
                  </div>
                </section>
              )}
              {accepted.length > 0 && (
                <section data-testid="invites-history-section">
                  <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-3 font-mono">History · {accepted.length}</div>
                  <div className="space-y-2">
                    {accepted.map((row) => (
                      <InviteCard key={row.id} row={row} readOnly />
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const InviteCard = ({ row, onRespond, busy, readOnly }) => {
  const pill = STATUS_PILL[row.status] || STATUS_PILL.invited;
  return (
    <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-5" data-testid="invite-card">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
        <div className="min-w-0 flex-1">
          <div className="text-base font-semibold text-[#0F0F0F] dark:text-white" data-testid="invite-campaign-name">{row.campaign?.campaign_name}</div>
          <div className="text-xs text-[#6B6B6B] dark:text-zinc-500">{row.campaign?.brand_name}</div>
        </div>
        <span className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-full inline-flex items-center gap-1 shrink-0"
              style={{ backgroundColor: `${pill.color}15`, color: pill.color, border: `1px solid ${pill.color}30` }}
              data-testid="invite-status-pill">
          <Clock size={9} /> {pill.label}
        </span>
      </div>
      {row.campaign?.brief && (
        <p className="text-sm text-[#6B6B6B] dark:text-zinc-400 mb-3 line-clamp-2">{row.campaign.brief}</p>
      )}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="inline-flex items-center gap-1 text-sm text-zinc-300">
          <IndianRupee size={13} className="text-[#00C972]" />
          <span className="font-mono font-semibold">{Number(row.campaign?.fixed_cost_per_creator || 0).toLocaleString('en-IN')}</span>
          <span className="text-xs text-[#6B6B6B] dark:text-zinc-500">fixed payout</span>
        </div>
        {!readOnly && ['invited', 'no_response'].includes(row.status) && (
          <div className="flex gap-2">
            <Button
              onClick={() => onRespond(row.id, 'decline')}
              disabled={busy}
              className="border border-black/[0.10] dark:border-white/10 bg-white/5 text-[#0F0F0F] dark:text-white hover:bg-white/10 rounded-full px-4 py-1.5 text-sm inline-flex items-center gap-1.5 disabled:opacity-40"
              data-testid="invite-decline-button"
            >
              <XCircle size={13} /> Decline
            </Button>
            <Button
              onClick={() => onRespond(row.id, 'accept')}
              disabled={busy}
              className="bg-[#00C972] text-[#0F0F0F] dark:text-white rounded-full px-5 py-1.5 text-sm font-semibold hover:bg-[#00B863] inline-flex items-center gap-1.5 disabled:opacity-40"
              data-testid="invite-accept-button"
            >
              <CheckCircle2 size={13} /> Accept
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
